//React
import React, { Fragment, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

//Pages
import MetricsHomePage from "Page/MetricsHomePage";
import { PageNotFound } from "Page/PageNotFound";
import ServiceHomePage from "Page/ServiceHomePage";
import { RegistrationDetailsPage } from "Page/registration-details/RegistrationDetailsPage";

//Components
import ServiceFooter from "Component/ServiceFooter";
import ServiceHeader from "Component/ServiceHeader";
import ErrorBoundary from "./ErrorBoundary";

//Util
import { LocalStorage } from "Core/storage";
import { Amplify, Auth } from "aws-amplify";
import { config } from "./amplifyConfig";

import "@amzn/awsui-global-styles/polaris.css";
import DefaultRedirection from "./components/DefaultRedirection";
import { DataAccessService } from "./core/api";
import { User } from "./core/user";
import {
  DELEGATIONS,
  FEATURE_FLAGS,
  INSTALL_STATUS,
  USER_DETAILS,
} from "./core/user/user";
import { getDelegations } from "./helpers/delegation";
import { getInstallationStatus } from "./helpers/get-installation-status";
import getBypasses from "./helpers/getBypasses";
import getCurrentUserDetails from "./helpers/getCurrentUserDetails";
import { isBypassesPhase2On } from "./helpers/isBypassesPhase2On";
import { isInternalUser } from "./helpers/isInternalUser";
import { isRuleManagementAccessible } from "./helpers/isRuleManagementAccessible";
import { isTopRopeFeatureOn } from "./helpers/isTopRopeFlagOn";
import { transferToLogin } from "./helpers/transferToLogin";
import { AllBypassesPage } from "./pages/bypasses/AllBypassesPage";
import { UnacknowledgedBypassesPage } from "./pages/bypasses/UnacknowledgedBypassesPage";
import { ComplianceAlertsPage } from "./pages/compliance-alerts/ComplianceAlertsPage";
import { DashboardPage } from "./pages/dashboard/DashboardPage";
import { Personal } from "./pages/dashboard/personal/Personal";
import { Reports } from "./pages/dashboard/reports/Reports";
import { Risks } from "./pages/dashboard/risks/Risks";
import { DelegationPage } from "./pages/delegation/DelegationPage";
import { RepoDetailPage } from "./pages/repo-rules/RepoDetailPage";
import { ReposPage } from "./pages/repo-rules/ReposPage";
import { RulesEnginePage } from "./pages/rules-engine/RulesEnginePage";
import { RulesPage } from "./pages/team-rules/RulesPage";

Amplify.configure({
  Auth: config,
});

function Router() {
  const [userLoaded, setUserLoaded] = useState(false);

  useEffect(() => {
    const authUser = async () => {
      let cognitoUser;
      try {
        cognitoUser = await Auth.currentAuthenticatedUser();
      } catch (e) {
        transferToLogin();
        return;
      }

      try {
        const client = new DataAccessService({
          baseURL: `${window.location.protocol}//${window.location.host}`,
        });

        const getFeatureFlags = client.get(`/featureflags`);
        const getUserDetails = getCurrentUserDetails(client, new User().userId);
        const getInstallStatus = getInstallationStatus();
        const getDelegation = getDelegations(client, {}, {});

        let [featureFlags, userDetails, installStatus, delegations] =
          await Promise.all([
            getFeatureFlags,
            getUserDetails,
            getInstallStatus,
            getDelegation,
          ]);

        LocalStorage.set(FEATURE_FLAGS, featureFlags);
        LocalStorage.set(INSTALL_STATUS, installStatus);
        LocalStorage.set(USER_DETAILS, userDetails);
        LocalStorage.set(DELEGATIONS, delegations);

        if (isTopRopeFeatureOn()) {
          await getBypasses();
        }
      } catch (e) {
        console.warn(`Error getting and storing user details - `, e);
      }

      setUserLoaded(true);
    };
    authUser();
  }, []);

  if (userLoaded) {
    return (
      <BrowserRouter forceRefresh={false}>
        <ErrorBoundary>
          <Fragment>
            <ServiceHeader />
            <div className="page">
              <Routes>
                <Route index element={<DefaultRedirection />} />
                <Route
                  exact
                  path="/installation"
                  element={<ServiceHomePage />}
                />
                {isInternalUser() && (
                  <Route
                    exact
                    path="/view_metrics"
                    element={<MetricsHomePage />}
                  />
                )}
                <Route
                  exact
                  path="/registration_details"
                  element={<RegistrationDetailsPage />}
                />
                <Route path="/dashboard" element={<DashboardPage />}>
                  <Route index element={<Personal />} />
                  <Route path="reports" element={<Reports />} />
                  <Route path="risks" element={<Risks />} />
                </Route>
                <Route path="/delegation" element={<DelegationPage />} />
                {isRuleManagementAccessible() && (
                  <Route path="/team_rules" element={<RulesPage />} />
                )}
                {isRuleManagementAccessible() && (
                  <Route path="/repo_rules" element={<ReposPage />} />
                )}
                {isRuleManagementAccessible() && (
                  <Route
                    path="/repo_allow_list"
                    element={<RulesEnginePage />}
                  />
                )}
                {isRuleManagementAccessible() && (
                  <Route path="/repo_rules/:id" element={<RepoDetailPage />} />
                )}
                {isRuleManagementAccessible() && (
                  <Route
                    path="/rules"
                    element={<Navigate to="/repo_allow_list" />}
                  />
                )}
                {!isRuleManagementAccessible() && (
                  <Route path="/rules" element={<RulesEnginePage />} />
                )}
                {isTopRopeFeatureOn() && (
                  <Route path="/bypasses/all" element={<AllBypassesPage />} />
                )}
                {isTopRopeFeatureOn() && (
                  <Route
                    path="/bypasses/unacknowledged"
                    element={<UnacknowledgedBypassesPage />}
                  />
                )}
                {isTopRopeFeatureOn() && isBypassesPhase2On() && (
                  <Route
                    path="/compliance_alerts"
                    element={<ComplianceAlertsPage />}
                  />
                )}
                <Route path="*" element={<PageNotFound />} />
              </Routes>
            </div>
            <ServiceFooter />
          </Fragment>
        </ErrorBoundary>
      </BrowserRouter>
    );
  } else {
    return (
      <div className="App">
        <div className="loader"></div>
      </div>
    );
  }
}

export default Router;
